/**
 * What `/fly` actually flies: the player's aeroplane, the opponent, and where
 * the two of them start.
 *
 * Kept apart from `sim-runner.ts` so the setup is a piece of data a test can
 * read and vary, rather than something baked into the runner's constructor.
 */

import {
  buildAircraftSpec,
  bombFlightSeconds,
  bombPositionAt,
  bombWind,
  encounterAir,
  normalizeVec3,
  roundPositionAt,
  FIXED_DT,
  GRAVITY_MPS2,
  type EncounterSetup,
  type EncounterSide,
  type EncounterState,
  type SimOptions,
  type Vec3,
} from "@red-opus/engine";

export const PLAYER_ID = "player";
export const ENEMY_ID = "enemy";

/**
 * The first flight: a Sopwith Camel already airborne at ~500 m in cruise, on
 * the Intermediate flight model with the resource and failure toggles the
 * manual's Intermediate preset turns on. MIDAIR COLLISIONS stays off because
 * the preset has it off — not, as this note used to say, because the encounter
 * layer cannot ram. It can (AD-017), and the original gates aeroplanes and
 * gasbags alike behind that one word.
 * `airspeedMps` is left to `createSim`'s default (0.8 × top speed ≈ 91 mph).
 */
export const CAMEL_FIRST_FLIGHT: SimOptions = {
  aircraft: "CAMEL",
  tier: "intermediate",
  realism: {
    gunJams: true,
    blackouts: true,
    carburetorFreezes: true,
    limitedFuel: true,
    limitedAmmunition: true,
    aircraftMayBeDamaged: true,
    midairCollisions: false,
  },
  altitudeM: 500,
  throttle: 0.85,
  seed: 1918,
};

/**
 * The same Camel, higher. A dogfight needs vertical room: the AI abandons a
 * manoeuvre and pulls out below `aiGroundClearanceM`, so starting a fight at
 * cruise height would spend it flying away from the ground rather than at each
 * other.
 */
export const ENCOUNTER_FLIGHT: SimOptions = { ...CAMEL_FIRST_FLIGHT, altitudeM: 1200 };

/**
 * Metres the Albatros starts astern of the player, and above them. The height
 * matters as much as the distance: an Albatros D.III tops out at 109 mph and a
 * Camel at 115, so in level flight it can never catch a Camel at full throttle.
 * What it has is the bounce — 400 m of potential energy to spend on a diving
 * pass. Outrunning it afterwards is a real and legitimate way out.
 */
export const ENEMY_STERN_M = 800;
export const ENEMY_ABOVE_M = 400;

const ENEMY_OFFSET: Vec3 = { x: 0, y: 0, z: ENEMY_STERN_M };

/**
 * A hostile Albatros D.III (`models/ALBA_D3` in the bake manifest) coming down
 * on the player from astern and above, flown by a Regular pilot — the middle
 * of the Mission Builder's four skill tiers.
 */
export const FIRST_ENCOUNTER: EncounterSetup = {
  seed: 19170421,
  aircraft: [
    { id: PLAYER_ID, side: "friendly", pilot: { kind: "player" }, flight: ENCOUNTER_FLIGHT },
    {
      id: ENEMY_ID,
      side: "hostile",
      pilot: { kind: "ai", skill: "regular" },
      flight: {
        aircraft: "ALBA_D3",
        tier: "intermediate",
        altitudeM: (ENCOUNTER_FLIGHT.altitudeM ?? 0) + ENEMY_ABOVE_M,
        airspeedMps: buildAircraftSpec("ALBA_D3").vmaxMps,
        throttle: 1,
        trimmed: true,
      },
      offsetM: ENEMY_OFFSET,
    },
  ],
};

/** An encounter of one — the same tick path with nobody else in the sky. */
export function soloEncounter(flight: SimOptions): EncounterSetup {
  return {
    seed: flight.seed ?? 1,
    aircraft: [{ id: PLAYER_ID, side: "friendly", pilot: { kind: "player" }, flight }],
  };
}

/**
 * One **visible** round in flight, ready to draw as a single pixel projectile.
 * There is no streak geometry and no fade: the engine's records fly dead
 * straight down the gun line (`packages/engine`'s `rounds.ts`), so all the
 * renderer needs is where each one is this frame and which way it is going.
 *
 * Only the tracers reach here. The engine simulates and collides every round
 * fired; one in three of them is a tracer and a tracer is the only round anyone
 * can see, so what is drawn is a third of what is in the air — which is the
 * belt the original loaded, not a thinning-out for performance.
 */
export interface RoundView {
  /** World-space position at the encounter's current tick. */
  readonly position: Vec3;
  /**
   * Unit direction of flight, fixed for the round's whole life. The renderer
   * lays the streak sprite along this direction's *apparent* (screen-space)
   * bearing — a round is a streak of motion, not a horizontal dash, and
   * orienting it off the camera's axes alone was the "aligned sideways" the
   * user reported on 2026-08-19 (`bullet-sprites.ts`'s `fillBulletPool`).
   */
  readonly direction: Vec3;
  readonly fromPlayer: boolean;
  /**
   * Which side the shooter was on — **not** which seat is watching.
   *
   * `fromPlayer` answers "is this mine", which is a different question: a
   * wingman's rounds are `fromPlayer: false` and `shooterSide: "friendly"`, and
   * ENHANCED EFFECTS' tracers need both to tell the player's own stream from
   * his flight's from the enemy's (`tracer-bolts.ts`'s `tracerTierFor`). The
   * authentic pixel path reads neither and never will — the original draws one
   * projectile colour for everyone.
   *
   * Resolved from the roster by `shooterId`, which is safe for the case that
   * looks unsafe: **the record outlives the shooter**. A round stays in the air
   * for its full lifetime after the aeroplane that fired it has been destroyed,
   * and `encounter-tick.ts` keeps a destroyed aeroplane in `state.aircraft`
   * with a changed `status` rather than removing it, so the lookup still finds
   * the side that fired. `ammunitionOf` in the engine resolves a shooter the
   * same way for the same reason.
   */
  readonly shooterSide: EncounterSide;
  /** The shooter's running round ordinal — which drawn gun breech it left. */
  readonly roundIndex: number;
  /**
   * Set on the falling bombs `appendBombViews` puts in this same list, absent
   * on every actual round.
   *
   * The authentic pixel-projectile path does not care — a bomb is drawn through
   * it exactly as a round is, deliberately, and that is unchanged in both
   * toggle states. ENHANCED EFFECTS' tracers (`tracer-bolts.ts`) do care: a
   * tracer is an incandescent round with a burning tail and a bomb is a
   * lump of iron, so the tracer pool skips these and the pixel pool keeps
   * drawing them.
   */
  readonly bomb?: true;
  /**
   * How far this round has flown since it left the barrel, in metres.
   *
   * The pixel path has no use for it — a 4-px dash is the same dash however
   * new the round is. A tracer's tail does: it is drawn *backwards* from the
   * round, so a round fired this tick with a full-length tail trails 26 m of
   * fire back through the aeroplane that fired it, which is the "tracers
   * originate behind the plane" the user reported on 2026-08-24. The tail can
   * only ever be as long as the round's own flight, and this is that bound.
   *
   * Absent on bombs, which the tracer pool skips anyway.
   */
  readonly travelledM?: number;
}

/** `playerId` is which seat the rounds are being drawn from — see `encounter-views.ts`. */
export function roundViews(state: EncounterState, playerId: string = PLAYER_ID): readonly RoundView[] {
  const tunables = state.aircraft[0]?.sim.config.tunables;
  const views: RoundView[] = [];
  if (state.rounds.length > 0 && tunables) {
    for (const round of state.rounds) {
      if (!round.tracer) continue;
      const position = roundPositionAt(round, state.tick, FIXED_DT, tunables);
      views.push({
        position,
        direction: round.direction,
        fromPlayer: round.shooterId === playerId,
        shooterSide: sideOf(state, round.shooterId),
        roundIndex: round.roundIndex,
        // Measured from the muzzle the round actually left, so it stays right
        // whatever `roundPositionAt` does between ticks.
        travelledM: Math.hypot(position.x - round.from.x, position.y - round.from.y, position.z - round.from.z),
      });
    }
  }
  appendBombViews(state, playerId, views);
  return views;
}

/**
 * Which side fired a round, by the shooter's id.
 *
 * `RoundRecord` carries no side of its own and deliberately is not given one:
 * `encounter-hash.ts` hashes a round field by field, so adding one would move
 * every recorded encounter hash in the suite for a renderer's benefit. The
 * roster already knows, and it still knows *after the shooter is dead* —
 * `encounter-tick.ts` rebuilds `state.aircraft` by pushing every entry with a
 * new `status`, never by dropping the destroyed ones, so a burst that arrives
 * after its author has gone down still resolves to the side that fired it.
 * That is the same guarantee `damageRaw` is carried on the round for, one step
 * further on.
 *
 * **Two rosters, because two kinds of thing shoot.** A Zeppelin's four gunners
 * and an escort sloop's two Maxims put ordinary `RoundRecord`s into the same
 * list an aeroplane does (`airship-pass.ts`, `ship-pass.ts`), and neither is on
 * `state.aircraft`. The target list is searched second, and it has to be
 * searched at all: a *friendly* ship firing at the aeroplane on the player's
 * tail would otherwise paint her tracers as a threat. It changes nothing that
 * was already drawn — every Zeppelin this port generates is hostile, which is
 * what the fallback was already giving them.
 *
 * The fallback is `"hostile"` rather than `"friendly"` on the principle that an
 * unidentifiable shooter is by default not one of yours: the safe error is to
 * paint a round as a threat, never to paint a threat as a friend.
 */
function sideOf(state: EncounterState, shooterId: string): EncounterSide {
  const aeroplane = state.aircraft.find((entry) => entry.id === shooterId);
  if (aeroplane) return aeroplane.side;
  return state.targets.find((entry) => entry.id === shooterId)?.side ?? "hostile";
}

/**
 * The bombs in the air, drawn through the same pixel-projectile pool the tracers
 * use. At the range a raid is watched from that is what a falling bomb looks
 * like, and it means what the screen shows is the *record* the crater was
 * resolved from rather than a decoration of it — the same discipline
 * `rounds.ts` states for gunfire.
 *
 * Unlike a round, a bomb's direction changes every tick: it leaves the rack on
 * the aeroplane's own vector and gravity bends it over, so the streak is laid
 * along the velocity at this tick rather than along a fixed line.
 */
function appendBombViews(state: EncounterState, playerId: string, into: RoundView[]): void {
  // The same air the bomb pass drops them through, so what is drawn is where the
  // record actually is — a bomb in wind is carried downwind as it falls
  // (AD-007k §9).
  const first = state.aircraft[0];
  const air = first ? encounterAir(first.sim) : undefined;
  for (const bomb of state.bombs) {
    const seconds = bombFlightSeconds(bomb, state.tick, FIXED_DT);
    const velocity: Vec3 = {
      x: bomb.velocity.x,
      y: bomb.velocity.y - GRAVITY_MPS2 * seconds,
      z: bomb.velocity.z,
    };
    into.push({
      position: bombPositionAt(bomb, state.tick, FIXED_DT, bombWind(air, bomb.from.y)),
      direction: normalizeVec3(velocity, { x: 0, y: -1, z: 0 }),
      fromPlayer: bomb.bomberId === playerId,
      // Filled for the shape's sake and read by nobody: a bomb stays on the
      // authentic pixel path in both toggle states, and that path has never
      // cared who dropped it.
      shooterSide: sideOf(state, bomb.bomberId),
      roundIndex: bomb.bombIndex,
      bomb: true,
    });
  }
}
